import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {MatAutocompleteSelectedEvent, MatAutocompleteTrigger} from '@angular/material/autocomplete';
import {Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged, pairwise, startWith} from 'rxjs/operators';
import {SearchWidgetState} from '../../typings/search-widget-state';
import {ToolsService} from 'src/app/services/tools.service';
import {SearchWidgetContentListItem} from 'src/app/types/SearchWidgetContentListItem';
import {SearchWidgetDataItem} from 'src/app/types/SearchWidgetDataItem';
import {SearchWidgetManagerService} from '../../../../services/search-widget/search-widget-manager.service';
import {SearchWidgetThemeEnum} from '../../enums/search-widget-theme.enum';
import {PlacesFreeSearchEnterHit} from '../../typings/places-free-search-enter-hit';
import {SearchWidgetLabelsEnum} from '../../enums/search-widget-labels.enum';
import {CssPositionEnum} from '../../../../enums/CssPositionEnum';
import {EventCategoryEnum} from '../../../../enums/GaTrackingEnums';
import {GtmEventEnum} from '../../../../enums/GoogleEnums';
import {SearchSuggestPayload} from '../../../../types/SearchSuggestPayload';
import {SearchSuggestTypesEnum} from '../../../../enums/SearchEnums';
import {SearchSuggestItem} from '../../../../types/SearchSuggestItem';
import {SearchPlacesData} from '../../../../types/SearchPlacesData';
import {SearchService} from '../../../../services/search/search.service';
import {DLPageTypeEnum} from '../../../../enums/DataLayerEnums';

@Component({
  selector: 'app-places-fake-dropdown-v2',
  templateUrl: './places-fake-dropdown-v2.component.html',
  styleUrls: ['./places-fake-dropdown-v2.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlacesFakeDropdownV2Component implements OnInit, OnDestroy {
  private static NumberOfSuggestionsToDisplay = 10;
  private static NumberOfPopularToDisplay = 6;
  isDropdownActive = input<boolean>(true);
  theme = input<SearchWidgetThemeEnum>(SearchWidgetThemeEnum.DEFAULT);
  pageType = input<DLPageTypeEnum>();
  changePlaceDropdownState = output<boolean | undefined>();
  freeSearchEnterHit = output<PlacesFreeSearchEnterHit>();
  freeSearchOptionSelected = output<string>();
  freeSearchClear = output<boolean>();
  @ViewChild(MatAutocompleteTrigger, {static: false})
  autocomplete: MatAutocompleteTrigger;
  @ViewChild('freeSearchInput')
  freeSearchInput;
  public freeSearchForm: UntypedFormGroup;
  public popularSuggestItems = signal<Array<SearchWidgetContentListItem>>(null);
  public filteredSuggestItems = signal<Array<SearchSuggestItem>>(null);
  protected readonly CssPositionEnum = CssPositionEnum;
  protected readonly SearchWidgetThemeEnum = SearchWidgetThemeEnum;
  protected readonly SearchWidgetLabelsEnum = SearchWidgetLabelsEnum;

  private searchService = inject(SearchService);
  public isLocationLoading = this.searchService.isLocationLoading;
  public placePanelOpen = this.searchService.placePanelOpen;
  public state = this.searchService.searchWidgetState;
  private placeSelected = this.searchService.placeSelected;
  private places = this.searchService.places;
  private searchState = this.searchService.searchState;
  private subscriptions: Array<Subscription> = [];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private tools: ToolsService,
    private searchWidgetManagerService: SearchWidgetManagerService,
    private _change: ChangeDetectorRef,
  ) {
    effect(
      () => {
        this.processPlaceSelected(this.placeSelected());
      },
      {allowSignalWrites: true},
    );

    effect(
      () => {
        this.processWidgetData(this.places());
      },
      {allowSignalWrites: true},
    );

    effect(
      () => {
        this.updateFreeSearchFieldValues(this.state());
      },
      {allowSignalWrites: true},
    );

    effect(
      () => {
        if (this.state() && this.state().placesFieldFocus === true) {
          this.setFieldFocus();
        }
      },
      {allowSignalWrites: true},
    );
  }

  public ngOnInit() {
    this.buildFreeSearchForm();
    this.setListeners();
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public onEnterHit(): void {
    this.autocomplete.closePanel();
    if (this.freeSearchForm.controls['term'].value === null) {
      this.searchByLocationClick();
      return;
    }

    this.freeSearchEnterHit.emit({
      term: this.tools.clone(this.freeSearchForm.controls['term'].value),
    });
    this.freeSearchForm.reset(
      {
        term: '',
      },
      {emitEvent: false},
    );
  }

  public onClear() {
    this.freeSearchForm.reset(
      {
        term: '',
      },
      {emitEvent: false},
    );
    this.freeSearchClear.emit(true);
  }

  public onDropdownClick($event: Event): void {
    this.autocomplete.closePanel();
    $event.stopPropagation();
    this.changePlaceDropdownState.emit(undefined);
  }

  public handleOptionSelected(event: MatAutocompleteSelectedEvent): void {
    if (event.option.value === null) {
      return;
    }
    this.freeSearchForm.setValue(
      {
        term: event.option.value || '',
      },
      {emitEvent: false},
    );
    this.freeSearchOptionSelected.emit(event.option.value);
  }

  public handleAutocompleteOpened() {
    this.changePlaceDropdownState.emit(false);
  }

  public handleAutocompleteClosed() {
    this.searchWidgetManagerService.setFocusOffPlacesField();
  }

  public handleFocus(): void {
    this.searchWidgetManagerService.loadPlaces();
  }

  public searchByLocationClick(): void {
    this.searchWidgetManagerService.registerGtmGa4Event(
      `${EventCategoryEnum.SEARCHNAV} - search by location click`,
      GtmEventEnum.CTA_CLICK,
      this.pageType(),
    );

    this.searchService.isLocationLoading.set(true);
    this.searchWidgetManagerService.getUserLocation(null, true, false, true);
  }

  private buildFreeSearchForm() {
    this.freeSearchForm = this.formBuilder.group(
      {
        term: [''],
      },
      {emitEvent: false},
    );
  }

  private setListeners() {
    this.subscriptions.push(
      this.freeSearchForm.valueChanges
        .pipe(debounceTime(500), distinctUntilChanged(), startWith(''), pairwise())
        .subscribe(([previous, current]) => {
          const previousTerm = previous.term;
          const currentTerm = current.term;

          if (previousTerm !== currentTerm) {
            this.updateSuggestions(currentTerm);
            this.onValueChanged(currentTerm);
          }
        }),
    );
  }

  private onValueChanged(term: string): void {
    if (this.state().autocompletePlaceCurrentValue !== term) {
      const state = this.tools.clone(this.state());
      state.autocompletePlaceValueChanged = true;
      state.autocompletePlaceCurrentValue = term;
      state.placeSearchValue = '';
      state.placeSearchValueLabel = '';

      // const newSearchState = this.tools.clone(this.searchState());
      // newSearchState.holdResultsReload = true;
      this.searchService.updateSearchWidgetState(state);
      // this.searchService.updateSearchState(newSearchState);
    }

    if (term === null && this.state().isLocationSearchActive) {
      const state = this.tools.clone(this.state());
      state.placeSearchValueLabel = SearchWidgetLabelsEnum.NARA_MIG;
      this.searchService.updateSearchWidgetState(state);
    }
    this._change.markForCheck();
  }

  private updateSuggestions(term: string): void {
    this.popularSuggestItems.set(null);
    this.filteredSuggestItems.set(null);
    if (this.tools.isNil(term) || term.length === 0 || term === SearchWidgetLabelsEnum.NARA_MIG) {
      this.updateSuggestionsWithPopularPlaces();
    } else {
      this.updateSuggestionsWithQuery(term);
    }
    this._change.markForCheck();
  }

  private updateSuggestionsWithPopularPlaces(): void {
    if (!this.tools.isNil(this.places())) {
      this.popularSuggestItems.set(
        this.places()
          .popularPlaces.slice(0, PlacesFakeDropdownV2Component.NumberOfPopularToDisplay)
          .map(
            (item: SearchWidgetDataItem): SearchWidgetContentListItem => ({
              id: item.id,
              name: item.name,
              urlPath: item.urlPath,
              caregiverUnitsCount: item.caregiverUnitsCount,
              columnIndex: 0,
            }),
          ),
      );
      this.filteredSuggestItems.set(null);
    }
  }

  private updateSuggestionsWithQuery(term: string): void {
    if (term.length > 0 && term !== SearchWidgetLabelsEnum.NARA_MIG) {
      const suggestPayload: SearchSuggestPayload = {
        term: encodeURIComponent(this.tools.clone(term).trim()),
        size: PlacesFakeDropdownV2Component.NumberOfSuggestionsToDisplay,
        types: [SearchSuggestTypesEnum.PLACE],
      };
      this.subscriptions.push(
        this.searchService.freeSearchSuggest(suggestPayload).subscribe(
          (result) => this.processSuggestData(result),
          (error) => console.error(error),
        ),
      );
    }
  }

  private processSuggestData(suggestions: SearchSuggestItem[]): void {
    const suggestionsWithLabels: SearchSuggestItem[] = [];
    suggestions.forEach((item) => {
      item.suggestLabel = this.searchWidgetManagerService.getSuggestTypeLabel(item);
      suggestionsWithLabels.push(item);
    });
    this.filteredSuggestItems.set(suggestionsWithLabels);
  }

  private processWidgetData(result: SearchPlacesData): void {
    // Guard - if widgetState is not yet populated let's stay with default
    if (result === null) {
      return;
    }
    this.updateSuggestions(this.state().placeSearchValueLabel);
  }

  private updateFreeSearchFieldValues(state: SearchWidgetState): void {
    // FIXME - if place suggestion desktop will not work
    // let placeTerm;
    // if (
    //   state.autocompletePlaceValueChanged === true &&
    //   this.freeSearchForm.controls['term'].value !==
    //     state.autocompletePlaceCurrentValue
    // ) {
    //   placeTerm = state.autocompletePlaceCurrentValue;
    // } else if (state.autocompletePlaceValueChanged === true) {
    //   placeTerm = state.autocompletePlaceCurrentValue;
    // } else {
    //   placeTerm = state.placeSearchValueLabel;
    // }
    // if (
    //   state.isLocationSearchActive &&
    //   this.tools.isNil(state.autocompletePlaceCurrentValue)
    // ) {
    //   placeTerm = state.placeSearchValueLabel;
    // }
    //
    // this.freeSearchForm.setValue(
    //   {
    //     term: placeTerm,
    //   },
    //   {emitEvent: false}
    // );
    // this.updateSuggestions(placeTerm);

    let newTerm: string = null;
    if (
      state.isLocationSearchActive &&
      (this.tools.isNil(state.autocompletePlaceCurrentValue) || state.autocompletePlaceCurrentValue === '')
    ) {
      newTerm = state.placeSearchValueLabel;
    } else if (this.freeSearchForm.controls['term'].value !== state.autocompletePlaceCurrentValue) {
      if (!state.autocompletePlaceValueChanged) {
        newTerm = state.placeSearchValueLabel;
      } else {
        newTerm = state.autocompletePlaceCurrentValue;
      }
    }

    if (!this.tools.isNil(newTerm)) {
      this.freeSearchForm.setValue(
        {
          term: newTerm || '',
        },
        {emitEvent: false},
      );
      this.updateSuggestions(newTerm);
    }
  }

  private processPlaceSelected(result: string): void {
    if (!this.tools.isNil(result)) {
      this.freeSearchForm.setValue(
        {
          term: result || '',
        },
        {emitEvent: false},
      );
    }
  }

  private setFieldFocus(): void {
    if (this.state().placesFieldFocus === true && !this.tools.isNil(this.freeSearchInput)) {
      this.freeSearchInput.nativeElement.focus();
    }
  }
}
